/**
 * Offline storage utilities using IndexedDB
 */

const DB_NAME = 'tsunami-report-db';
const DB_VERSION = 1;
const OFFLINE_STORE = 'offline-reports';
const OFFLINE_IMAGES_STORE = 'offline-images';

/**
 * Initialize the IndexedDB database
 * @returns {Promise<IDBDatabase>} The initialized database
 */
export const initDatabase = () => {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      console.error('IndexedDB not supported in this browser');
      reject(new Error('This browser doesn\'t support IndexedDB'));
      return;
    }

    console.log('Opening IndexedDB database...');
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      const db = event.target.result;
      console.log('IndexedDB opened successfully');
      
      // Log the object stores
      console.log('Object stores:', Array.from(db.objectStoreNames));
      
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      console.log('IndexedDB upgrade needed, creating stores...');
      const db = event.target.result;
      
      // Create store for offline report data
      if (!db.objectStoreNames.contains(OFFLINE_STORE)) {
        console.log('Creating offline reports store');
        const store = db.createObjectStore(OFFLINE_STORE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }
      
      // Create store for offline report images
      if (!db.objectStoreNames.contains(OFFLINE_IMAGES_STORE)) {
        console.log('Creating offline images store');
        const imageStore = db.createObjectStore(OFFLINE_IMAGES_STORE, { keyPath: 'id', autoIncrement: true });
        imageStore.createIndex('reportId', 'reportId', { unique: false });
      }
      
      console.log('IndexedDB setup complete');
    };
    
    // Handle blocked events (other tabs using the same DB)
    request.onblocked = (event) => {
      console.warn('IndexedDB blocked - please close other tabs with this app open');
    };
  });
};

/**
 * Store a report for offline submission
 * @param {Object} reportData - The report data
 * @param {File} imageFile - Optional image file
 * @returns {Promise<number>} The ID of the stored report
 */
export const storeOfflineReport = async (reportData, imageFile = null) => {
  try {
    const db = await initDatabase();
    
    // If there's an image, we need to read it first before starting the transaction
    let imageBuffer = null;
    let imageFilename = null;
    let imageType = null;
    
    if (imageFile) {
      console.log('Reading image file before starting transaction:', imageFile.name);
      imageBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (err) => {
          console.error('Error reading image file:', err);
          reject(reader.error);
        };
        reader.readAsArrayBuffer(imageFile);
      });
      imageFilename = imageFile.name;
      imageType = imageFile.type;
      console.log('Image read successfully, size:', imageBuffer.byteLength);
    }
    
    // Now start the transaction
    const tx = db.transaction([OFFLINE_STORE, OFFLINE_IMAGES_STORE], 'readwrite');
    const store = tx.objectStore(OFFLINE_STORE);
    
    // Prepare the report data with status
    const reportToStore = {
      data: reportData, // Ensure reportData is nested under 'data' key
      timestamp: Date.now(),
      status: 'pending' // pending, syncing, completed, failed
    };
    
    console.log('Saving report to IndexedDB:', reportToStore);
    
    // Store the report data and image in the same transaction
    return new Promise((resolve, reject) => {
      // Add event listener for transaction completion
      tx.oncomplete = () => {
        console.log('Transaction completed successfully');
        resolve(reportId);
      };
      
      tx.onerror = (event) => {
        console.error('Transaction error:', event.target.error);
        reject(event.target.error);
      };
      
      // Store the report
      let reportId;
      const reportRequest = store.add(reportToStore);
      
      reportRequest.onsuccess = (event) => {
        reportId = event.target.result;
        console.log('Report stored with ID:', reportId);
        
        // If we have an image to store, do it now within the same transaction
        if (imageBuffer) {
          console.log('Storing image for report:', reportId);
          const imageStore = tx.objectStore(OFFLINE_IMAGES_STORE);
          
          // Store complete image metadata for proper reconstruction later
          const imageRequest = imageStore.add({
            reportId,
            imageData: imageBuffer,
            filename: imageFilename,
            type: imageType,
            size: imageBuffer.byteLength,
            lastModified: Date.now()
          });
          
          imageRequest.onsuccess = () => {
            console.log('Image stored successfully for report:', reportId, {
              size: imageBuffer.byteLength,
              type: imageType,
              filename: imageFilename
            });
          };
          
          imageRequest.onerror = (err) => {
            console.error('Error storing image:', err);
            // Don't reject here - we still want to store the report even if image fails
          };
        }
      };
      
      reportRequest.onerror = (err) => {
        console.error('Error adding report to store:', err);
        reject(err.target.error);
      };
    });
  } catch (error) {
    console.error('Error storing offline report:', error);
    throw error;
  }
};

/**
 * Get all pending offline reports
 * @returns {Promise<Array>} Array of pending reports
 */
export const getPendingReports = async () => {
  try {
    const db = await initDatabase();
    const tx = db.transaction(OFFLINE_STORE, 'readonly');
    const store = tx.objectStore(OFFLINE_STORE);
    
    // Get all reports first to log what's actually in the database
    const allReportsRequest = store.getAll();
    const allReports = await new Promise((resolve, reject) => {
      allReportsRequest.onsuccess = () => resolve(allReportsRequest.result);
      allReportsRequest.onerror = () => reject(allReportsRequest.error);
    });
    
    console.log('All reports in IndexedDB:', allReports);
    
    // Get pending reports
    const index = store.index('status');
    const pendingReports = await new Promise((resolve, reject) => {
      const request = index.getAll('pending');
      request.onsuccess = () => {
        console.log('Pending reports found:', request.result);
        resolve(request.result);
      };
      request.onerror = (err) => {
        console.error('Error getting pending reports:', err);
        reject(request.error);
      };
    });
    
    return pendingReports;
  } catch (error) {
    console.error('Error getting pending reports:', error);
    return [];
  }
};

/**
 * Get an image for a report
 * @param {number} reportId - The report ID
 * @returns {Promise<Object|null>} The image data or null if not found
 */
export const getReportImage = async (reportId) => {
  try {
    const db = await initDatabase();
    const tx = db.transaction(OFFLINE_IMAGES_STORE, 'readonly');
    const store = tx.objectStore(OFFLINE_IMAGES_STORE);
    const index = store.index('reportId');
    
    console.log(`Getting image for report ID: ${reportId}`);
    
    return new Promise((resolve, reject) => {
      const request = index.get(reportId);
      request.onsuccess = () => {
        const imageData = request.result;
        if (imageData) {
          console.log(`Found image for report ${reportId}:`, {
            size: imageData.imageData?.byteLength || 0,
            type: imageData.type,
            filename: imageData.filename
          });
        } else {
          console.log(`No image found for report ${reportId}`);
        }
        resolve(imageData || null);
      };
      request.onerror = (err) => {
        console.error(`Error retrieving image for report ${reportId}:`, err);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error getting report image:', error);
    return null;
  }
};

/**
 * Update the status of an offline report
 * @param {number} reportId - The report ID
 * @param {string} status - The new status (pending, syncing, completed, failed)
 * @param {string} error - Optional error message
 * @returns {Promise<void>}
 */
export const updateReportStatus = async (reportId, status, error = null) => {
  try {
    const db = await initDatabase();
    const tx = db.transaction(OFFLINE_STORE, 'readwrite');
    const store = tx.objectStore(OFFLINE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.get(reportId);
      
      request.onsuccess = () => {
        const report = request.result;
        if (!report) {
          console.warn(`Report with ID ${reportId} not found, it may have been deleted already`);
          resolve(); // Resolve without error if report is not found
          return;
        }
        
        report.status = status;
        if (error) {
          report.error = error;
        }
        
        const updateRequest = store.put(report);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = (err) => {
          console.error(`Error updating report ${reportId}:`, err);
          reject(updateRequest.error);
        };
      };
      
      request.onerror = (err) => {
        console.error(`Error getting report ${reportId}:`, err);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('Error updating report status:', error);
    // Don't throw, just log the error to avoid breaking the sync chain
  }
};

/**
 * Delete a report by ID
 * @param {number} reportId - The report ID
 * @returns {Promise<void>}
 */
export const deleteReport = async (reportId) => {
  try {
    const db = await initDatabase();
    const tx = db.transaction([OFFLINE_STORE, OFFLINE_IMAGES_STORE], 'readwrite');
    const store = tx.objectStore(OFFLINE_STORE);
    
    // Check if report exists before trying to delete
    const checkRequest = await new Promise((resolve, reject) => {
      const req = store.get(reportId);
      req.onsuccess = () => resolve(req);
      req.onerror = (err) => {
        console.error(`Error checking if report ${reportId} exists:`, err);
        reject(req.error);
      };
    });
    
    if (!checkRequest.result) {
      console.warn(`Report with ID ${reportId} not found, skipping deletion`);
      return;
    }
    
    const imageStore = tx.objectStore(OFFLINE_IMAGES_STORE);
    const imageIndex = imageStore.index('reportId');
    
    // Delete the report
    await new Promise((resolve, reject) => {
      const request = store.delete(reportId);
      request.onsuccess = () => {
        console.log(`Successfully deleted report ${reportId}`);
        resolve();
      };
      request.onerror = (err) => {
        console.error(`Error deleting report ${reportId}:`, err);
        reject(request.error);
      };
    });
    
    // Find and delete associated image
    try {
      const imageRequest = imageIndex.getKey(reportId);
      await new Promise((resolve, reject) => {
        imageRequest.onsuccess = () => {
          const imageKey = imageRequest.result;
          if (imageKey) {
            const deleteRequest = imageStore.delete(imageKey);
            deleteRequest.onsuccess = () => {
              console.log(`Successfully deleted image for report ${reportId}`);
              resolve();
            };
            deleteRequest.onerror = (err) => {
              console.error(`Error deleting image for report ${reportId}:`, err);
              reject(deleteRequest.error);
            };
          } else {
            resolve();
          }
        };
        imageRequest.onerror = (err) => {
          console.error(`Error finding image for report ${reportId}:`, err);
          reject(imageRequest.error);
        };
      });
    } catch (imageError) {
      // Don't fail if we can't delete the image
      console.warn(`Error deleting image for report ${reportId}:`, imageError);
    }
  } catch (error) {
    console.error('Error deleting report:', error);
    // Don't throw, just log the error to avoid breaking the sync chain
  }
}; 